import {
  getBlockAbove,
  getEndPoint,
  getPluginType,
  getPointAfter,
  getPointBefore,
  getStartPoint,
  isCollapsed,
  isRangeInSameBlock,
  moveSelection,
  replaceNodeChildren,
  select,
  withoutNormalizing,
  getTableGridAbove,
  getCellTypes,
  ELEMENT_TABLE,
  ELEMENT_DEFAULT,
} from '@udecode/plate';
import { Point } from 'slate';

/**
 * Return true if:
 * - at start/end of a cell.
 * - next to a table cell. Move selection to the table cell.
 */
export const preventDeleteTableCell = (
  editor,
  {
    unit,
    reverse,
  },
) => {
  const { selection } = editor;

  const getPoint = !reverse ? getStartPoint : getEndPoint;
  const getNextPoint = !reverse ? getPointBefore : getPointAfter;

  if (isCollapsed(selection)) {
    const cellEntry = getBlockAbove(editor, {
      match: { type: getCellTypes(editor) },
    });

    if (cellEntry) {
      // Prevent deleting cell at the start or end of a cell
      const [, cellPath] = cellEntry;

      const start = getPoint(editor, cellPath);

      if (selection && Point.equals(selection.anchor, start)) {
        return true;
      }
    } else {
      // Prevent deleting cell when selection is before or after a table
      const nextPoint = getNextPoint(editor, selection, { unit });

      const nextCellEntry = getBlockAbove(editor, {
        match: { type: getCellTypes(editor) },
        at: nextPoint,
      });
      if (nextCellEntry) {
        moveSelection(editor, { reverse: !reverse });
        return true;
      }
    }
  }
};

/**
 * Prevent cell deletion.
 */
export const withDeleteTable = (
  editor,
) => {
  const { deleteBackward, deleteForward, deleteFragment } = editor;

  editor.deleteBackward = (unit) => {
    if (preventDeleteTableCell(editor, { unit })) {
      // Delete other types of blocks within a cell. e.g. Lists
      const [block] = getBlockAbove(editor, { mode: 'lowest' });
      const cellTypes = getCellTypes(editor);
      const { type } = block || {};
      if (type && !cellTypes.includes(type) && type !== ELEMENT_DEFAULT) {
        return deleteBackward(unit);
      }
      return;
    };

    return deleteBackward(unit);
  };

  editor.deleteForward = (unit) => {
    if (preventDeleteTableCell(editor, { unit, reverse: true })) return;

    return deleteForward(unit);
  };

  editor.deleteFragment = (direction) => {
    isRangeInSameBlock(editor, {
      match: (n) => n.type === getPluginType(editor, ELEMENT_TABLE),
    });

    if (
      isRangeInSameBlock(editor, {
        match: (n) => n.type === getPluginType(editor, ELEMENT_TABLE),
      })
    ) {
      const cellEntries = getTableGridAbove(editor, { format: 'cell' });
      if (cellEntries.length > 1) {
        withoutNormalizing(editor, () => {
          cellEntries.forEach(([, cellPath]) => {
            replaceNodeChildren(editor, {
              at: cellPath,
              nodes: editor.blockFactory(),
            });
          });

          // set back the selection
          select(editor, {
            anchor: getStartPoint(editor, cellEntries[0][1]),
            focus: getEndPoint(editor, cellEntries[cellEntries.length - 1][1]),
          });
        });

        return;
      }
    }

    deleteFragment(direction);
  };

  return editor;
};
